const DB_NAME = 'UserCreateDB';
const DB_VERSION = 1;
const FILES_STORE = 'files';

interface StoredFile {
  id: string;
  file: File;
  timestamp: number;
}

const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(FILES_STORE)) {
        const store = db.createObjectStore(FILES_STORE, { keyPath: 'id' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

export const saveFileToIndexedDB = async (
  id: string,
  file: File,
): Promise<void> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([FILES_STORE], 'readwrite');
    const store = transaction.objectStore(FILES_STORE);

    const storedFile: StoredFile = {
      id,
      file,
      timestamp: Date.now(),
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.put(storedFile);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
    console.log('💾 File saved to IndexedDB:', id, file.name);
  } catch (error) {
    console.error('Failed to save file to IndexedDB:', error);
    throw error;
  }
};

export const loadFileFromIndexedDB = async (
  id: string,
): Promise<File | null> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([FILES_STORE], 'readonly');
    const store = transaction.objectStore(FILES_STORE);

    const storedFile: StoredFile = await new Promise<StoredFile>(
      (resolve, reject) => {
        const request = store.get(id);
        request.onsuccess = () => {
          if (request.result) {
            resolve(request.result);
          } else {
            resolve(null as any);
          }
        };
        request.onerror = () => reject(request.error);
      },
    );

    db.close();

    if (storedFile && storedFile.file) {
      return storedFile.file;
    }

    return null;
  } catch (error) {
    console.error('Failed to load file from IndexedDB:', error);
    return null;
  }
};

export const removeFileFromIndexedDB = async (id: string): Promise<void> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([FILES_STORE], 'readwrite');
    const store = transaction.objectStore(FILES_STORE);

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
    console.log('🗑️ File removed from IndexedDB:', id);
  } catch (error) {
    console.error('Failed to remove file from IndexedDB:', error);
    throw error;
  }
};

export const clearAllFilesFromIndexedDB = async (): Promise<void> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([FILES_STORE], 'readwrite');
    const store = transaction.objectStore(FILES_STORE);

    await new Promise<void>((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
    console.log('🧹 All files cleared from IndexedDB');
  } catch (error) {
    console.error('Failed to clear files from IndexedDB:', error);
    throw error;
  }
};

export const getStoredFileIds = async (): Promise<string[]> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([FILES_STORE], 'readonly');
    const store = transaction.objectStore(FILES_STORE);

    const ids: string[] = await new Promise<string[]>((resolve, reject) => {
      const request = store.getAllKeys();
      request.onsuccess = () => resolve(request.result as string[]);
      request.onerror = () => reject(request.error);
    });

    db.close();
    return ids;
  } catch (error) {
    console.error('Failed to get stored file IDs:', error);
    return [];
  }
};

export const loadAllUserCreateFiles = async (): Promise<
  Record<string, File>
> => {
  try {
    const fileIds = await getStoredFileIds();
    const files: Record<string, File> = {};

    for (const fileId of fileIds) {
      if (fileId.startsWith('object-')) {
        const file = await loadFileFromIndexedDB(fileId);
        if (file) {
          files[fileId] = file;
        }
      }
    }

    return files;
  } catch (error) {
    console.error('Failed to load user create files:', error);
    return {};
  }
};

export const clearAllUserUpdateFilesFromIndexedDB = async (
  userId?: number,
): Promise<void> => {
  try {
    const fileIds = await getStoredFileIds();
    const prefix = userId ? `object-${userId}` : 'object-';

    for (const fileId of fileIds) {
      if (fileId.startsWith(prefix)) {
        await removeFileFromIndexedDB(fileId);
      }
    }
  } catch (error) {
    console.error('Failed to clear user update files from IndexedDB:', error);
    throw error;
  }
};

export const loadAllUserUpdateFiles = async (
  userId?: number,
): Promise<Record<string, File>> => {
  try {
    const fileIds = await getStoredFileIds();
    const files: Record<string, File> = {};
    const prefix = userId ? `object-${userId}` : 'object-';

    for (const fileId of fileIds) {
      if (fileId.startsWith(prefix)) {
        const file = await loadFileFromIndexedDB(fileId);
        if (file) {
          files[fileId] = file;
        }
      }
    }

    return files;
  } catch (error) {
    console.error('Failed to load user update files:', error);
    return {};
  }
};
